import { FC, useCallback, useEffect, useRef, useState } from "react";
import { AssetAmountField } from "@/components/AssetAmountField";
import { EstimateBar, LimitPriceField, OrderTypeTabs, TradeSideToggle, TradeSummary } from "./SwapPanelSections";
import { TradingPaneState } from "./TradingPaneState";
import { useTradeForm } from "./useTradeForm";
import type { OrderType, TradeSide } from "./useTradingViewState";
import type { Market } from "@module-bindings/types";
import { toast } from "sonner";

type DropdownOrderType =
  | "tp-sl"
  | "conditional"
  | "oco"
  | "trailing-stop"
  | "chase-limit"
  | "scaled-order"
  | "twap"
  | "iceberg";

const ORDER_TYPE_LABELS: Record<DropdownOrderType, string> = {
  "tp-sl": "TP/SL",
  conditional: "Conditional",
  oco: "OCO",
  "trailing-stop": "Trailing Stop",
  "chase-limit": "Chase Limit",
  "scaled-order": "Scaled Order",
  twap: "TWAP",
  iceberg: "Iceberg",
};

const ORDER_MENU_OPTIONS: DropdownOrderType[] = [
  "tp-sl",
  "conditional",
  "oco",
  "trailing-stop",
  "chase-limit",
  "scaled-order",
  "twap",
  "iceberg",
];

interface SwapPanelProps {
  selectedMarket: Market | null;
  tradeSide: TradeSide;
  onTradeSideChange: (side: TradeSide) => void;
  orderType: OrderType;
  onOrderTypeChange: (type: OrderType) => void;
  limitPrice: string;
  onLimitPriceChange: (price: string) => void;
  onDeposit?: (assetId?: string) => void;
  depositDisabled?: boolean;
}

export const SwapPanel: FC<SwapPanelProps> = ({
  selectedMarket,
  tradeSide,
  onTradeSideChange,
  orderType,
  onOrderTypeChange,
  limitPrice,
  onLimitPriceChange,
  onDeposit,
  depositDisabled = false,
}) => {
  const [isOrderMenuOpen, setIsOrderMenuOpen] = useState(false);
  const orderMenuRef = useRef<HTMLDivElement | null>(null);
  const {
    swapAmount,
    sliderValue,
    spendToken,
    quoteLabel,
    spendTokenOptions,
    spendBalance,
    hasLiquidity,
    maxMarketDisplay,
    estimatedReceiveLabel,
    receiveAmount,
    summaryRows,
    accentTextClass,
    accentSurfaceClass,
    sliderFillClass,
    sliderDotClass,
    sliderThumbStyle,
    ctaClass,
    depositCtaClass,
    ctaDisabled,
    ctaLabel,
    handleSliderChange,
    setAmountFromPercent,
    handleAmountChange,
    handleSelectToken,
    handleCtaClick,
  } = useTradeForm({
    selectedMarket,
    tradeSide,
    onTradeSideChange,
    orderType,
    limitPrice,
    onLimitPriceChange,
    onDeposit,
    depositDisabled,
  });

  // Auto-switch to limit when there's no liquidity in the current direction
  useEffect(() => {
    if (selectedMarket && !hasLiquidity && orderType === "market") {
      onOrderTypeChange("limit");
    }
  }, [selectedMarket, hasLiquidity, orderType, onOrderTypeChange]);

  const handleSelectOrderMenuOption = useCallback((type: DropdownOrderType) => {
    setIsOrderMenuOpen(false);
    toast(`${ORDER_TYPE_LABELS[type]} isn't available yet.`);
  }, []);

  if (!selectedMarket) {
    return (
      <div className="sr-panel flex min-h-[26rem] flex-col">
        <TradingPaneState
          eyebrow="Trade Ticket"
          title="Choose a market"
          message="Pick a trading pair from the market switcher to place an order."
        />
      </div>
    );
  }

  return (
    <div className="sr-panel flex flex-col gap-4 px-4 py-4">
      <TradeSideToggle value={tradeSide} onChange={onTradeSideChange} />

      <OrderTypeTabs
        value={orderType}
        onChange={onOrderTypeChange}
        hasLiquidity={hasLiquidity}
        selectedMarket={!!selectedMarket}
        menuSlot={(
          <div
            ref={orderMenuRef}
            className="relative"
            onBlur={(event) => {
              if (!orderMenuRef.current?.contains(event.relatedTarget as Node | null)) {
                setIsOrderMenuOpen(false);
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                setIsOrderMenuOpen(false);
              }
            }}
          >
            <button
              type="button"
              className="sr-focus-ring flex items-center gap-1 rounded-md py-1 text-sm font-medium text-sr-neutral-500 transition-colors hover:text-sr-neutral-300"
              aria-haspopup="menu"
              aria-expanded={isOrderMenuOpen}
              aria-label="Advanced order type menu"
              onClick={() => setIsOrderMenuOpen((open) => !open)}
            >
              Chase Limit
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {isOrderMenuOpen && (
              <>
                <button
                  type="button"
                  aria-label="Close advanced order type menu"
                  className="fixed inset-0 z-10 cursor-default"
                  onClick={() => setIsOrderMenuOpen(false)}
                />
                <div
                  role="menu"
                  className="absolute left-0 top-full z-20 mt-2 min-w-[180px] rounded-2xl border border-white/[0.08] bg-[#171717] p-2 shadow-[0_20px_60px_rgba(0,0,0,0.45)]"
                >
                  {ORDER_MENU_OPTIONS.map((type) => {
                    const isDefaultOption = type === "chase-limit";
                    return (
                      <button
                        key={type}
                        type="button"
                        role="menuitem"
                        className={`sr-focus-ring flex w-full items-center justify-between rounded-xl px-3 py-3 text-left text-base transition-colors ${
                          isDefaultOption
                            ? "bg-white/[0.08] text-white"
                            : "text-white hover:bg-white/[0.04]"
                        }`}
                        onClick={() => handleSelectOrderMenuOption(type)}
                      >
                        <span>{ORDER_TYPE_LABELS[type]}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      />

      <div className="flex flex-col gap-3">
        {orderType === "limit" && (
          <LimitPriceField
            quoteLabel={quoteLabel}
            value={limitPrice}
            onChange={onLimitPriceChange}
          />
        )}

        <div className="flex items-center justify-between text-sm text-sr-neutral-400">
          <span>Pay with</span>
          <span className="font-medium text-white">{spendToken?.label ?? "—"}</span>
        </div>

        <div className="flex items-center justify-between text-sm text-sr-neutral-400">
          <span>Available ({spendToken?.label ?? "Asset"})</span>
          <span className="text-white">{spendBalance ? spendBalance.formatted : "0.00"}</span>
        </div>

        <AssetAmountField
          amount={swapAmount}
          label="Amount"
          onAmountChange={handleAmountChange}
          token={{ assetId: spendToken?.key ?? "__none__", name: spendToken?.label ?? "Select" }}
          tokens={spendTokenOptions}
          onSelectToken={handleSelectToken}
          rightTopContent={
            <div className="flex gap-1.5">
              {([["25%", 25], ["50%", 50], ["MAX", 100]] as const).map(([label, pct]) => (
                <button
                  key={label}
                  onClick={() => setAmountFromPercent(pct)}
                  className={`sr-focus-ring rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors ${accentSurfaceClass}`}
                >
                  {label}
                </button>
              ))}
            </div>
          }
          amountClassName="text-[26px]"
          surfaceColor="#2F1C25"
          tokenSurfaceColor="#2F1C25"
          className="rounded-[20px]"
          bottomContent={
            <div className="relative px-1 pt-1">
              <div className="relative flex h-6 items-center">
                <div className="absolute inset-x-0 h-[2px] rounded-full bg-sr-neutral-700" />
                <div
                  className={`absolute h-[2px] rounded-full ${sliderFillClass}`}
                  style={{ width: `${sliderValue}%` }}
                />
                <div className="pointer-events-none absolute inset-x-0 flex justify-between">
                  {[0, 25, 50, 75, 100].map((value) => (
                    <div
                      key={value}
                      className={`h-2 w-2 rounded-full border-2 transition-colors ${
                        sliderValue >= value
                          ? sliderDotClass
                          : "border-sr-neutral-700 bg-sr-espresso"
                      }`}
                    />
                  ))}
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={sliderValue}
                  onChange={handleSliderChange}
                  className="absolute inset-0 w-full cursor-pointer opacity-0"
                  aria-label="Order size percentage"
                />
                <div
                  className="pointer-events-none absolute h-4 w-4 rounded-full border-[3px] border-sr-espresso"
                  style={{
                    ...sliderThumbStyle,
                    left: `calc(${sliderValue}% - 8px)`,
                  }}
                />
              </div>
            </div>
          }
        />

        <EstimateBar
          orderType={orderType}
          maxMarketDisplay={maxMarketDisplay}
          spendTokenLabel={spendToken?.label}
          receiveAmount={receiveAmount}
          estimatedReceiveLabel={estimatedReceiveLabel}
        />

        <TradeSummary rows={summaryRows} accentTextClass={accentTextClass} />
      </div>

      <button
        className={`sr-focus-ring w-full rounded-xl py-3 font-display text-lg font-medium tracking-tight transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
          spendBalance && spendBalance.raw > 0n ? ctaClass : depositCtaClass
        }`}
        disabled={ctaDisabled}
        onClick={handleCtaClick}
      >
        {ctaLabel}
      </button>
    </div>
  );
};
